import React, { useState, useEffect } from 'react';
import PropTypes from "prop-types";
import { useSelector, useDispatch } from "react-redux";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import { getTemplateList, getTemplateReferences, updatePageAndGetList, updateSortAndGetList } from "../../actions/templateListActions";
import { openDrawer } from "../../actions/drawerActions";
import QuestionCreateEditDrawer, { questionCreateEditDrawerProps } from "../drawers/QuestionCreateEditDrawerHook";
import TableViewHook from "../../containers/TableViewHook";
import TableGridHook from "../../containers/TableGridHook";
import ReviewDrawer, { reviewDrawerProps } from "../drawers/ReviewDrawerHook";
import ReferencesDrawerHook, { referencesDrawerProps } from "../drawers/ReferencesDrawerHook";
import FilterPopOverHook from "../dialogs/filters/FilterPopOverHook";
import ArgoTemplateListCreateFabHook from "../common/ArgoTemplateListCreateFabHook";
import ArgoListTitleAndLoadingAnimationHook from "../common/ArgoListTitleAndLoadingAnimationHook";
import ArgoScreenSizePopUpHook from "../common/ArgoScreenSizePopUpHook";
import { getLabel, supported } from "../../constants/questionTypeConstants";
import * as metadataTemplateTypes from "../../constants/metadataTemplateTypes";
import * as TEMPLATE from "../../constants/templateConstants";
import * as SYSTEM from "../../constants/systemConstants";
import { QD_URL } from "../../constants/systemConstants";
import formatCopyText from "../../utilities/formatCopyText";
import * as portalShared from "../../portal/shared/metadataConstantsAndUtilities";
import {getMetadataDefinitions} from "../../actions/metadataDefinitionActions";


function QuestionListHook(props) {
    const [loading, setLoading] = useState(false);
    const [filter, setFilter] = useState(false);
    const [closePopUp, setClosePopUp] = useState(false);

    const dispatch = useDispatch();
    const questionTemplateList = useSelector(state => state.questionTemplateList);
    const filterState = useSelector(state => state.filter[TEMPLATE.FILTER.current.question] || {});
    const keyMetricArr = useSelector(state => {
        let KMList = []
        if (!state.metadataDefinition[metadataTemplateTypes.QuestionTemplate].collection) {
            KMList = ["All", ...state.metadataDefinition[metadataTemplateTypes.QuestionTemplate].filter(def => {
                return def.name === portalShared.KEY_METRIC_CATEGORY_QUESTION
            })[0].listValues]
        }
        return KMList
    });

    useEffect(() => {
        setLoading(true);
        dispatch(getTemplateList(filterState, TEMPLATE.TYPE.QUESTION, TEMPLATE.MODE.CURRENT));
        dispatch(getMetadataDefinitions(metadataTemplateTypes.QuestionTemplate));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dispatch]);

    useEffect(() => {
        // Called twice, first pass set filter false, then on second call list is loaded and loading animation can be stopped
        if (filter) {
            setFilter(false);
        } else if (loading) {
            if (SYSTEM.USE_TIMER_DELAY) {
                // NOTE: Only use for local testing of loading render animation, before using for PRODUCTION set SYSTEM.USE_TIMER_DELAY_AMOUNT = false in systemConstants.js
                setTimeout(delayedSetLoadingFalse, SYSTEM.USE_TIMER_DELAY_AMOUNT); // 1000ms = 1 second, // Used to test loading animation rendering
            } else {
                setLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filter, questionTemplateList]);

    // Used to test loading animation rendering
    const delayedSetLoadingFalse = () => {
        setLoading(false);
    };

    const setLoadingStateTrue = (callback, parm1 = null) => {
        // If a parameter is passed then pass it to call back, should be the template of the item to be saved edit or created
        if (parm1 === null) {
            setLoading(true);
            setFilter(false);
            callback();
        } else {
            setLoading(true);
            setFilter(false);
            callback(parm1);
        }
    };

    const onPageChange = (page, size, event) => {
        setLoading(true);
        setFilter(true);
        dispatch(updatePageAndGetList(page, size, filterState, TEMPLATE.TYPE.QUESTION, TEMPLATE.MODE.CURRENT, event));
    };

    const onSortChange = (sort, direction) => {
        setLoading(true);
        setFilter(true);
        dispatch(updateSortAndGetList(sort, direction, filterState, TEMPLATE.TYPE.QUESTION, TEMPLATE.MODE.CURRENT));
    };

    const openEdit = (templateKey, id, templateMode, notUsed, row) => {
        if (!templateKey && props.match && props.match.params) {
            templateKey = props.match.params.templateKey;
        }

        dispatch(openDrawer(questionCreateEditDrawerProps, <QuestionCreateEditDrawer templateKey={templateKey} templateId={id} filter={filterState} templateMode={templateMode} row={row} setLoadingState={setLoadingStateTrue} />));
    };

    const openCreate = () => {
        dispatch(openDrawer(questionCreateEditDrawerProps, <QuestionCreateEditDrawer createMode={true} filter={filterState} setLoadingState={setLoadingStateTrue} />));
    };

    const openQuestionReviewDrawer = (templateKey, id, name, row, event) => {
        if (!templateKey && props.match && props.match.params) {
            templateKey = props.match.params.templateKey;
        }
        formatCopyText(row, event, "Question:");
        dispatch(openDrawer(reviewDrawerProps, <ReviewDrawer templateId={id} templateKey={templateKey} templateType={TEMPLATE.TYPE.QUESTION}/>));
    };

    const viewReferences = (templateKey, id, p1, p2, row) => {
        dispatch(getTemplateReferences(TEMPLATE.TYPE.QUESTION, id));
        dispatch(openDrawer(referencesDrawerProps, <ReferencesDrawerHook templateId={id} templateType={TEMPLATE.TYPE.QUESTION} row={row} title="Question:" />));
    };

    const handleClosePopUp = () => {
        localStorage.setItem("hasSeenWelcomeMessage", "true");
        setClosePopUp(true);
    };

    const customActions = [
        {
            func: openQuestionReviewDrawer,
            label: "Review"
        },
        {
            func: openEdit,
            label: "Edit"
        },
        {
            func: viewReferences,
            label: "References"
        }
    ];

    // Used by the versions list and stored in the version filters
    const defaultActions = {
        edit: openEdit,
        create: openCreate,
        review: openQuestionReviewDrawer
    };

    const supportedTypes = {};
    Object.keys(supported).forEach(key => {
        supportedTypes[key] = getLabel[key];
    });

    return (
        <div id="QuestionList-container-div" style={{ height: "100%", padding: "15px", marginTop: '64px' }}>
            {closePopUp ? "" : <ArgoScreenSizePopUpHook closeFunction={handleClosePopUp} />}

            <ArgoListTitleAndLoadingAnimationHook fileName="QuestionList" listTitle="Questions" loading={loading} showOutstandingAlert={false} filter={filter} />

            <div style={{ clear: "both" }}></div>

            <Card id="QuestionList-card"
                variant="outlined"
                sx={{
                    border: "1px solid lightgray", height: "100%", boxShadow: "2px 4px whitesmoke"
                }}>
                <CardContent id="QuestionList-card-content">

                    <TableViewHook
                        id="QuestionList-table-view"
                        templateType={TEMPLATE.TYPE.QUESTION}
                        templateMode={TEMPLATE.MODE.CURRENT}
                        setLoadingState={setLoadingStateTrue}
                        filter={<FilterPopOverHook page="questions"
                            templateType={TEMPLATE.TYPE.QUESTION}
                            templateMode={TEMPLATE.MODE.CURRENT}
                            filterName={TEMPLATE.FILTER.current.question}
                            submitValue={getTemplateList}
                            statuses={["All", "Draft", "Published"]}
                            showQuestionType={true}
                            showKeyMetric={true}
                            keyMetricArr={keyMetricArr}
                            types={supportedTypes}
                            setLoadingState={setLoadingStateTrue}
                            showSelectTag={true} parentName={"QuestionList"}
                        />}
                    >

                        <TableGridHook
                            id="QuestionList-table-grid"
                            displayType={"questions"}
                            dataSource={questionTemplateList}
                            nameDrawer={openQuestionReviewDrawer}
                            link={QD_URL + "/question-editor/"}
                            customActions={customActions}
                            pageState={filterState}
                            onPageChange={onPageChange}
                            sortState={filterState}
                            onSortChange={onSortChange}
                            type={metadataTemplateTypes.QuestionTemplate}
                            templateType={TEMPLATE.TYPE.QUESTION}
                            templateMode={TEMPLATE.MODE.CURRENT}
                            defaultActions={defaultActions}
                            showType={true}
                            showReferencesAndVersionsSameColumn={true}
                        />
                    </TableViewHook>

                    <ArgoTemplateListCreateFabHook
                        fileName="QuestionList"
                        fieldName="fab"
                        hoverText="Create Question"
                        onClick={openCreate}
                    />

                </CardContent>

            </Card>

        </div>
    );
}

QuestionListHook.propTypes = {
    match: PropTypes.object,
    location: PropTypes.object,
};

export default QuestionListHook;